/**
 * DispatchService — Pro-Themed Dispatch Package Generator
 *
 * Generates professional, enterprise-grade dispatch packages (ZIP or Excel)
 * with Navy/Emerald themed styling, sectioned brand layouts, and granular
 * column mapping for both Brand Readiness and Menu Item sheets.
 *
 * Features:
 *  - Professional Excel template (Navy header, Emerald status, grey borders)
 *  - Sectioned brand layout (brand-by-brand with visual section headers)
 *  - Dual column configuration (Brand Readiness + Menu Items)
 *  - ZIP packaging with Excel + /images folder
 *  - Full config sync (unchecked = column deleted from output)
 *  - Base64 → binary image conversion for ZIP assets
 */

import JSZip from 'jszip';
import * as XLSX from 'xlsx-js-style';
import type { Campaign, CampaignSubmission, SubmissionItem, ReviewAction } from '../types';
import { DiscountType, FundingType } from '../types';
import type { BrandReadinessEntry } from '../utils';
import { ProfessionalExcelFormatter } from './ProfessionalExcelFormatter';

// ─── Configuration Types ─────────────────────────────────────────────────────

/** Menu Item columns the admin can toggle on/off */
export interface ColumnSelection {
  itemName: boolean;
  itemNameAR: boolean;
  description: boolean;
  descriptionAR: boolean;
  optionGroupsEN: boolean;
  optionGroupsAR: boolean;
  originalPrice: boolean;
  discountedPrice: boolean;
  internalBrandId: boolean;
  imageUrl: boolean;
  itemId: boolean;
}

/** Brand Readiness columns the admin can toggle on/off */
export interface ReadinessColumnSelection {
  brandName: boolean;
  branches: boolean;
  itemCount: boolean;
  target: boolean;
  balance: boolean;
  status: boolean;
  currentStage: boolean;
  submittedBy: boolean;
}

export const ALL_COLUMNS_ON: ColumnSelection = {
  itemName: true,
  itemNameAR: true,
  description: true,
  descriptionAR: true,
  optionGroupsEN: true,
  optionGroupsAR: true,
  originalPrice: true,
  discountedPrice: true,
  internalBrandId: false,
  imageUrl: false,
  itemId: false,
};

export const ALL_READINESS_COLUMNS_ON: ReadinessColumnSelection = {
  brandName: true,
  branches: true,
  itemCount: true,
  target: true,
  balance: true,
  status: true,
  currentStage: true,
  submittedBy: true,
};

/** Audit History columns the admin can toggle on/off */
export interface AuditColumnSelection {
  brand: boolean;
  itemName: boolean;
  event: boolean;
  reviewer: boolean;
  adminNote: boolean;
  department: boolean;
  timestamp: boolean;
}

export const ALL_AUDIT_COLUMNS_ON: AuditColumnSelection = {
  brand: true,
  itemName: true,
  event: true,
  reviewer: true,
  adminNote: true,
  department: true,
  timestamp: true,
};

export interface DispatchConfig {
  includeImages: boolean;
  /** Sheet toggles — each controls whether that Excel sheet/tab is generated */
  includeOverview: boolean;
  includeBrandReadiness: boolean;
  includeApprovedMenu: boolean;
  includeBrandSheets: boolean;
  brandWiseCategorization: boolean;
  /** Brand IDs to include (empty = all ready brands) */
  selectedBrandIds: string[];
  /** Menu Item column visibility */
  columns: ColumnSelection;
  /** Brand Readiness column visibility */
  readinessColumns: ReadinessColumnSelection;
  /** Whether to include the Audit History sheet */
  includeAuditHistory: boolean;
  /** Audit History column visibility */
  auditColumns: AuditColumnSelection;
  /** When ON, menu sheets use relational modifier code links instead of inline add-ons */
  useRelationalModifierCodes?: boolean;
}

export const DEFAULT_DISPATCH_CONFIG: DispatchConfig = {
  includeImages: true,
  includeOverview: true,
  includeBrandReadiness: true,
  includeApprovedMenu: true,
  includeBrandSheets: true,
  brandWiseCategorization: true,
  selectedBrandIds: [],
  columns: { ...ALL_COLUMNS_ON },
  readinessColumns: { ...ALL_READINESS_COLUMNS_ON },
  includeAuditHistory: true,
  auditColumns: { ...ALL_AUDIT_COLUMNS_ON },
};

// ─── Result Type ─────────────────────────────────────────────────────────────

export interface DispatchResult {
  imageCount: number;
  brandCount: number;
  itemCount: number;
  fileName: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmtDateExcel(ts: number): string {
  return new Date(ts).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function sanitizeFilename(s: string): string {
  return s.replace(/[^a-zA-Z0-9_\-. ]/g, '').replace(/\s+/g, '_');
}

function calcDiscountedPrice(originalPrice: number, campaign: Campaign): number {
  if (campaign.discountType === DiscountType.PERCENTAGE) {
    return Math.round(originalPrice * (1 - campaign.discountValue / 100) * 100) / 100;
  }
  return Math.max(0, Math.round((originalPrice - campaign.discountValue) * 100) / 100);
}

const xlTrunc = (v: string, max = 32767) => (v.length > max ? v.slice(0, max - 3) + '...' : v);

/** Filter out discarded items from a submission */
function liveItems(sub: CampaignSubmission): SubmissionItem[] {
  return sub.items.filter(it => it.itemStatus !== 'DISCARDED');
}

/** Format campaign date range for the Duration column (e.g. "17 March to 21 March") */
function formatDuration(campaign: Campaign): string {
  const from = new Date(campaign.validFrom);
  const to = new Date(campaign.validTo);
  const fromDay = from.getDate();
  const toDay = to.getDate();
  const toMonth = to.toLocaleDateString('en-GB', { month: 'long' });
  if (from.getMonth() === to.getMonth() && from.getFullYear() === to.getFullYear()) {
    return `${fromDay} ${toMonth} to ${toDay} ${toMonth}`;
  }
  const fromMonth = from.toLocaleDateString('en-GB', { month: 'long' });
  return `${fromDay} ${fromMonth} to ${toDay} ${toMonth}`;
}

/** Sanitize Excel sheet name: remove invalid chars, max 31 chars, ensure uniqueness */
function safeSheetName(name: string, usedNames: Set<string>): string {
  let sn = name.replace(/[\\/*?:\[\]]/g, '').trim().slice(0, 31) || 'Sheet';
  while (usedNames.has(sn)) { sn = sn.slice(0, 28) + `_${usedNames.size}`; }
  usedNames.add(sn);
  return sn;
}

/**
 * Convert a Base64 data-URL (e.g. "data:image/png;base64,iVBOR…") to a Uint8Array.
 * Returns null if the string isn't a valid data-URL.
 */
function base64ToUint8Array(dataUrl: string): { data: Uint8Array; ext: string } | null {
  const match = dataUrl.match(/^data:image\/(\w+);base64,(.+)$/);
  if (!match) return null;
  const ext = match[1] === 'jpeg' ? 'jpg' : match[1];
  const raw = atob(match[2]);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return { data: arr, ext };
}

// ─── Column Header/Row Builders ──────────────────────────────────────────────

/**
 * Build the ordered list of menu item column headers for the Approved Menu sheet.
 * Note: 'Image URL' is excluded from this sheet — images go in the ZIP folder.
 */
function buildMenuHeaders(cols: ColumnSelection): string[] {
  const h: string[] = [];
  if (cols.itemName) h.push('Item EN');
  if (cols.itemNameAR) h.push('Item AR');
  if (cols.description) h.push('Description EN');
  if (cols.descriptionAR) h.push('Description AR');
  if (cols.optionGroupsEN) h.push('Option Groups EN');
  if (cols.optionGroupsAR) h.push('Option Groups AR');
  if (cols.originalPrice) h.push('Original Price');
  if (cols.discountedPrice) h.push('After Discount');
  if (cols.internalBrandId) h.push('Brand ID');
  if (cols.itemId) h.push('Item ID');
  h.push('Duration');
  return h;
}

/** Build a menu item row array matching the column header order */
function buildMenuRow(
  brandId: string,
  item: SubmissionItem,
  campaign: Campaign,
  cols: ColumnSelection,
  durationText: string,
): (string | number)[] {
  const none = 'None';
  const r: (string | number)[] = [];
  if (cols.itemName) r.push(xlTrunc(item.name || none));
  if (cols.itemNameAR) r.push(xlTrunc(item.nameAR || none));
  if (cols.description) r.push(xlTrunc(item.description || none));
  if (cols.descriptionAR) r.push(xlTrunc(item.descriptionAR || none));
  if (cols.optionGroupsEN) {
    const groups = (item.importedOptions ?? []).map(g => {
      const opts = g.options.map(o => o.nameEN || none).join(', ');
      return `${g.nameEN || none}: ${opts || none}`;
    }).join(' | ');
    r.push(xlTrunc(groups || none));
  }
  if (cols.optionGroupsAR) {
    const groups = (item.importedOptions ?? []).map(g => {
      const opts = g.options.map(o => o.nameAR || none).join(', ');
      return `${g.nameAR || none}: ${opts || none}`;
    }).join(' | ');
    r.push(xlTrunc(groups || none));
  }
  if (cols.originalPrice) r.push(item.price);
  if (cols.discountedPrice) r.push(calcDiscountedPrice(item.price, campaign));
  if (cols.internalBrandId) r.push(brandId);
  if (cols.itemId) r.push(item.id);
  r.push(durationText);
  return r;
}

/** Build Brand Readiness headers from admin's readiness column selection */
function buildReadinessHeaders(rc: ReadinessColumnSelection): string[] {
  const h: string[] = ['#'];
  if (rc.brandName) h.push('Brand');
  if (rc.branches) h.push('Branches');
  if (rc.status) h.push('Status');
  if (rc.target) h.push('Target Count');
  if (rc.itemCount) h.push('Submitted Count');
  if (rc.balance) h.push('Balance');
  if (rc.currentStage) h.push('Current Stage');
  if (rc.submittedBy) h.push('Submitted By');
  return h;
}

/** Build a Brand Readiness row array matching the readiness column header order */
function buildReadinessRow(
  idx: number,
  b: BrandReadinessEntry,
  targetCount: number,
  rc: ReadinessColumnSelection,
): (string | number)[] {
  const r: (string | number)[] = [idx];
  if (rc.brandName) r.push(b.brandName);
  if (rc.branches) r.push((b.branches ?? []).map(br => br.branchName + (br.hasMenu ? ' ✓' : ' ✗')).join(', ') || '—');
  if (rc.status) {
    const readiness = b.itemCount >= targetCount
      ? (b.itemCount > targetCount ? 'Excess' : 'Complete')
      : b.itemCount > 0 ? 'Partial' : 'Missing';
    r.push(readiness);
  }
  if (rc.target) r.push(targetCount);
  if (rc.itemCount) r.push(b.itemCount);
  if (rc.balance) r.push(Math.max(0, targetCount - b.itemCount));
  if (rc.currentStage) r.push(b.currentStage ?? (b.status === 'READY' ? 'Complete' : b.status === 'MISSING' ? 'Not Submitted' : '—'));
  if (rc.submittedBy) r.push(buildLifecycleChain(b.submission));
  return r;
}

// ─── Lifecycle Chain Helper ──────────────────────────────────────────────────

/**
 * Build the approval lifecycle chain for a submission:
 * [Manager Name] → [F&B Approved By] → [F&P Approved By] → [Sales Approved By]
 */
function buildLifecycleChain(sub?: CampaignSubmission): string {
  if (!sub) return '—';
  const parts: string[] = [sub.submitterName || '—'];
  const stages = ['F&B', 'F&P', 'Sales'];
  for (const stage of stages) {
    const approval = (sub.reviewHistory ?? []).find(
      r => r.action === 'APPROVE' && r.departmentName?.toLowerCase().includes(stage.toLowerCase()),
    );
    parts.push(approval ? approval.reviewerName : '—');
  }
  return parts.join(' → ');
}

// ─── Audit History Column Builders ───────────────────────────────────────────

/** Build Audit History headers from admin's audit column selection */
function buildAuditHeaders(ac: AuditColumnSelection): string[] {
  const h: string[] = ['#'];
  if (ac.brand) h.push('Brand');
  if (ac.itemName) h.push('Item Name');
  if (ac.event) h.push('Event');
  if (ac.reviewer) h.push('Reviewer');
  if (ac.adminNote) h.push('Admin Note');
  if (ac.department) h.push('Department');
  if (ac.timestamp) h.push('Timestamp');
  return h;
}

/** Build a single Audit History row */
function buildAuditRow(
  idx: number,
  brandName: string,
  itemName: string,
  action: ReviewAction,
  ac: AuditColumnSelection,
): (string | number)[] {
  const r: (string | number)[] = [idx];
  if (ac.brand) r.push(brandName);
  if (ac.itemName) r.push(xlTrunc(itemName));
  if (ac.event) {
    const labels: Record<string, string> = {
      APPROVE: 'Approved',
      REJECT: 'Rejected',
      UNLOCK: 'Unlocked for Fix',
      RESUBMIT: 'Resubmitted',
    };
    r.push(labels[action.action] ?? action.action);
  }
  if (ac.reviewer) r.push(action.reviewerName || '—');
  if (ac.adminNote) r.push(xlTrunc(action.note || '—'));
  if (ac.department) r.push(action.departmentName || '—');
  if (ac.timestamp) r.push(fmtDateExcel(action.timestamp));
  return r;
}

// ─── DispatchService ─────────────────────────────────────────────────────────

export class DispatchService {
  /**
   * Generate the complete dispatch package.
   * If includeImages is ON → produces a .zip (Excel + /images folder)
   * If includeImages is OFF → produces a standalone .xlsx download
   */
  static async generate(
    campaign: Campaign,
    readySubmissions: CampaignSubmission[],
    brandEntries: BrandReadinessEntry[],
    config: DispatchConfig,
  ): Promise<DispatchResult> {
    const Fmt = ProfessionalExcelFormatter;

    // Filter submissions by selected brands (empty = all)
    const filteredSubs = config.selectedBrandIds.length > 0
      ? readySubmissions.filter(s => config.selectedBrandIds.includes(s.brandId))
      : readySubmissions;

    const wb = XLSX.utils.book_new();
    const usedNames = new Set<string>();
    let imageCount = 0;
    const durationText = formatDuration(campaign);

    // ── Sheet 1: Campaign Overview (optional) ─────────────────────────────
    if (config.includeOverview) {
      const fundingLabel =
        campaign.fundingType === FundingType.CO_FUNDED ? 'Co-Funded'
        : campaign.fundingType === FundingType.FULLY_FUNDED ? 'Fully Funded'
        : 'No Funding';

      const overviewData = [
        { Field: 'Campaign Name', Value: campaign.name },
        { Field: 'Campaign Type', Value: campaign.discountType === DiscountType.PERCENTAGE ? 'Percentage Discount' : 'Flat Discount' },
        { Field: 'Discount', Value: campaign.discountType === DiscountType.PERCENTAGE ? `${campaign.discountValue}%` : `QAR ${campaign.discountValue}` },
        { Field: 'Start Date', Value: fmtDateExcel(campaign.validFrom) },
        { Field: 'End Date', Value: fmtDateExcel(campaign.validTo) },
        { Field: 'Funding Type', Value: fundingLabel },
        ...(campaign.fundingType === FundingType.CO_FUNDED ? [
          { Field: 'Sterling Share', Value: `${campaign.sterlingSharePct ?? 0}%` },
          { Field: 'Partner Share', Value: `${campaign.partnerSharePct ?? 0}%` },
        ] : []),
        { Field: 'Aggregators', Value: xlTrunc(campaign.aggregators.join(', ')) },
        { Field: 'Brands Ready', Value: xlTrunc(filteredSubs.map(s => s.brandName).join(', ')) },
        { Field: 'Ready / Total Brands', Value: `${filteredSubs.length} / ${brandEntries.length}` },
        { Field: 'Total Items', Value: String(filteredSubs.reduce((acc, s) => acc + liveItems(s).length, 0)) },
        ...(campaign.requiredItemCount && campaign.requiredItemCount > 0 ? [
          { Field: 'Target Items per Brand', Value: String(campaign.requiredItemCount) },
        ] : []),
        ...(campaign.importantRequirements ? [{ Field: 'Requirements', Value: xlTrunc(campaign.importantRequirements) }] : []),
        { Field: 'Export Date', Value: fmtDateExcel(Date.now()) },
      ];

      const ws1 = XLSX.utils.json_to_sheet(overviewData);
      ws1['!cols'] = [{ wch: 25 }, { wch: 55 }];
      Fmt.applyOverviewStyle(ws1);
      XLSX.utils.book_append_sheet(wb, ws1, safeSheetName('Campaign Overview', usedNames));
    }

    // ── Sheet 2: Brand Readiness Summary (config-driven columns) ──────────
    if (config.includeBrandReadiness) {
      const rc = config.readinessColumns;
      const targetCount = campaign.requiredItemCount ?? 1;
      const headers = buildReadinessHeaders(rc);

      // Filter brand entries by selectedBrandIds (same filter as other sheets)
      const filteredBrands = config.selectedBrandIds.length > 0
        ? brandEntries.filter(b => config.selectedBrandIds.includes(b.brandId))
        : brandEntries;

      const aoa: (string | number)[][] = [headers];
      if (filteredBrands.length === 0) {
        const placeholder = new Array(headers.length).fill('—') as (string | number)[];
        placeholder[0] = 1;
        aoa.push(placeholder);
      } else {
        filteredBrands.forEach((b, i) => {
          aoa.push(buildReadinessRow(i + 1, b, targetCount, rc));
        });
      }

      const ws2 = XLSX.utils.aoa_to_sheet(aoa);
      ws2['!cols'] = Fmt.autoFitColumns(ws2);
      Fmt.applyHeaderRow(ws2, headers.length);
      Fmt.applyDataBorders(ws2);
      // Colour the Status column if present
      if (rc.status) {
        const statusIdx = headers.indexOf('Status');
        if (statusIdx >= 0) Fmt.applyStatusColouring(ws2, statusIdx);
      }
      if (aoa.length > 1) {
        ws2['!autofilter'] = { ref: `A1:${XLSX.utils.encode_col(headers.length - 1)}${aoa.length}` };
      }
      XLSX.utils.book_append_sheet(wb, ws2, safeSheetName('Brand Readiness', usedNames));
    }

    // ── Sheet 3: Approved Menu Items (sectioned brand layout) ─────────────
    if (config.includeApprovedMenu) {
      this.buildSectionedMenuSheet(wb, usedNames, campaign, filteredSubs, config);
    }

    // ── Sheet 4+: Individual brand sheets ─────────────────────────────────
    if (config.includeBrandSheets && filteredSubs.length > 0) {
      for (const sub of filteredSubs) {
        const items = liveItems(sub);
        if (items.length === 0) continue;
        const cols = config.columns;
        const headers = buildMenuHeaders(cols);
        const aoa: (string | number)[][] = [headers];
        items.forEach((item, idx) => {
          aoa.push(buildMenuRow(sub.brandId, item, campaign, cols, idx === 0 ? durationText : ''));
        });

        const ws = XLSX.utils.aoa_to_sheet(aoa);
        ws['!cols'] = Fmt.autoFitColumns(ws);
        Fmt.applyHeaderRow(ws, headers.length);
        Fmt.applyDataBorders(ws);
        if (aoa.length > 1) {
          ws['!autofilter'] = { ref: `A1:${XLSX.utils.encode_col(headers.length - 1)}${aoa.length}` };
        }
        XLSX.utils.book_append_sheet(wb, ws, safeSheetName(sub.brandName, usedNames));
      }
    }

    // ── Audit History Sheet ────────────────────────────────────────────────
    if (config.includeAuditHistory) {
      const ac = config.auditColumns;
      const auditHeaders = buildAuditHeaders(ac);
      const auditAoa: (string | number)[][] = [auditHeaders];
      let auditIdx = 0;

      for (const sub of filteredSubs) {
        const history = sub.reviewHistory ?? [];
        for (const action of history) {
          // For UNLOCK actions, resolve item names from the submission items
          if (action.action === 'UNLOCK' && action.unlockedItemIds && action.unlockedItemIds.length > 0) {
            for (const itemId of action.unlockedItemIds) {
              const item = sub.items.find(it => it.id === itemId);
              auditIdx++;
              auditAoa.push(buildAuditRow(auditIdx, sub.brandName, item?.name ?? itemId, action, ac));
            }
          } else {
            auditIdx++;
            auditAoa.push(buildAuditRow(auditIdx, sub.brandName, '—', action, ac));
          }
        }
      }

      if (auditAoa.length === 1) {
        const empty = new Array(auditHeaders.length).fill('—') as (string | number)[];
        empty[0] = 1;
        auditAoa.push(empty);
      }

      const wsAudit = XLSX.utils.aoa_to_sheet(auditAoa);
      wsAudit['!cols'] = Fmt.autoFitColumns(wsAudit);
      Fmt.applyHeaderRow(wsAudit, auditHeaders.length);
      Fmt.applyDataBorders(wsAudit);
      // Colour the Event column
      if (ac.event) {
        const eventIdx = auditHeaders.indexOf('Event');
        if (eventIdx >= 0) Fmt.applyStatusColouring(wsAudit, eventIdx);
      }
      if (auditAoa.length > 1) {
        wsAudit['!autofilter'] = { ref: `A1:${XLSX.utils.encode_col(auditHeaders.length - 1)}${auditAoa.length}` };
      }
      XLSX.utils.book_append_sheet(wb, wsAudit, safeSheetName('Review History', usedNames));
    }

    // ── Generate Excel Buffer ─────────────────────────────────────────────
    const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    const baseName = sanitizeFilename(campaign.name);
    const totalItems = filteredSubs.reduce((acc, s) => acc + liveItems(s).length, 0);

    if (config.includeImages) {
      // ── ZIP mode: Excel + /images folder ──────────────────────────────
      const zip = new JSZip();
      const excelBuf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      zip.file(`${baseName}_Master_Dispatch.xlsx`, excelBuf);

      // Add images — filenames always match item name regardless of column config
      const imgFolder = zip.folder('images')!;
      for (const sub of filteredSubs) {
        for (const item of liveItems(sub)) {
          if (!item.imageUrl) continue;
          const parsed = base64ToUint8Array(item.imageUrl);
          if (!parsed) continue;
          const imgName = `${sanitizeFilename(sub.brandName)}_${sanitizeFilename(item.name)}.${parsed.ext}`;
          imgFolder.file(imgName, parsed.data);
          imageCount++;
        }
      }

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const fileName = `${baseName}_Master_Dispatch_${dateStr}.zip`;
      const link = document.createElement('a');
      link.href = URL.createObjectURL(zipBlob);
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);

      return { imageCount, brandCount: filteredSubs.length, itemCount: totalItems, fileName };
    } else {
      const fileName = `${baseName}_Master_Dispatch_${dateStr}.xlsx`;
      XLSX.writeFile(wb, fileName);
      return { imageCount: 0, brandCount: filteredSubs.length, itemCount: totalItems, fileName };
    }
  }

  // ── Sectioned Brand Layout — brand-by-brand with orange/yellow template ──

  private static buildSectionedMenuSheet(
    wb: XLSX.WorkBook,
    usedNames: Set<string>,
    campaign: Campaign,
    readySubmissions: CampaignSubmission[],
    config: DispatchConfig,
  ): void {
    const Fmt = ProfessionalExcelFormatter;
    const cols = config.columns;
    const itemHeaders = buildMenuHeaders(cols);
    const colCount = itemHeaders.length;
    const durationText = formatDuration(campaign);
    const discountLabel = campaign.discountType === DiscountType.PERCENTAGE
      ? `-${campaign.discountValue}%`
      : `-QAR ${campaign.discountValue}`;

    // Find the index of the 'Item EN' column for left-alignment
    const itemsColIndex = itemHeaders.indexOf('Item EN');

    // Build the sheet row-by-row using aoa (array-of-arrays)
    const aoa: (string | number)[][] = [];
    const brandSectionRows: number[] = [];   // row indices of orange brand header rows
    const columnHeaderRows: number[] = [];   // row indices of yellow column header rows
    const dataRowIndices: number[] = [];     // row indices of item data rows

    for (let si = 0; si < readySubmissions.length; si++) {
      const sub = readySubmissions[si];
      const items = liveItems(sub);

      // Spacer row between brands (not before first)
      if (si > 0) {
        aoa.push(new Array(colCount).fill(''));
      }

      // Row A: Brand Header — "MCDONALD'S -10%" (merged, orange)
      const brandRow = new Array(colCount).fill('') as (string | number)[];
      brandRow[0] = `${sub.brandName.toUpperCase()} ${discountLabel}`;
      aoa.push(brandRow);
      brandSectionRows.push(aoa.length - 1);

      // Row B: Column Headers — "Items | Original Price | After Discount | Duration"
      aoa.push([...itemHeaders]);
      columnHeaderRows.push(aoa.length - 1);

      // Data Rows: Each approved item with duration on every row
      for (const item of items) {
        aoa.push(buildMenuRow(sub.brandId, item, campaign, cols, durationText));
        dataRowIndices.push(aoa.length - 1);
      }

      if (items.length === 0) {
        const empty = new Array(colCount).fill('') as (string | number)[];
        empty[0] = 'No items';
        aoa.push(empty);
        dataRowIndices.push(aoa.length - 1);
      }
    }

    // Fallback for empty campaign
    if (aoa.length === 0) {
      aoa.push(itemHeaders);
      const empty = new Array(colCount).fill('') as (string | number)[];
      empty[0] = 'No items';
      aoa.push(empty);
    }

    const ws = XLSX.utils.aoa_to_sheet(aoa);

    // ── Styling ──

    // 1. Orange brand section header rows (merged across all columns)
    for (const rowIdx of brandSectionRows) {
      Fmt.applyBrandSectionRow(ws, rowIdx, colCount);
    }

    // 2. Yellow column header rows (bold, centered)
    for (const rowIdx of columnHeaderRows) {
      Fmt.applyHeaderRow(ws, colCount, rowIdx);
    }

    // 3. Data rows: white bg, black borders, Items=left, others=center
    Fmt.applyMenuDataRows(ws, dataRowIndices, colCount, itemsColIndex >= 0 ? itemsColIndex : 0);

    // 4. Auto-fit column widths
    ws['!cols'] = Fmt.autoFitColumns(ws);

    XLSX.utils.book_append_sheet(wb, ws, safeSheetName('Approved Menu Items', usedNames));
  }
}

